import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllActiveSessions,
  ActiveSession,
} from '../../services/sessionsAPI';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import SearchIcon from '@mui/icons-material/Search';
import DevicesIcon from '@mui/icons-material/Devices';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import PublicIcon from '@mui/icons-material/Public';
import ComputerIcon from '@mui/icons-material/Computer';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import styles from './AdminActiveSessionsPage.module.css';

type SortField = 'lastActivity' | 'loginTime' | 'usuario';
type SortDir = 'asc' | 'desc';

const AdminActiveSessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'diretor' | 'supervisor' | 'user'>('all');
  const [sortField, setSortField] = useState<SortField>('lastActivity');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const loadSessions = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const sessionsData = await getAllActiveSessions();
      setSessions(sessionsData.sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadSessions(false);
    }, 3000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  useEffect(() => {
    let filtered = [...sessions];

    if (searchTerm) {
      filtered = filtered.filter(session =>
        session.usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.userId.toString().includes(searchTerm) ||
        session.ip?.includes(searchTerm)
      );
    }

    if (filterRole !== 'all') {
      filtered = filtered.filter(session => session.role === filterRole);
    }

    // Sort
    filtered.sort((a, b) => {
      let valA: string | number;
      let valB: string | number;

      if (sortField === 'usuario') {
        valA = a.usuario.toLowerCase();
        valB = b.usuario.toLowerCase();
      } else {
        valA = new Date(a[sortField]).getTime();
        valB = new Date(b[sortField]).getTime();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredSessions(filtered);
  }, [sessions, searchTerm, filterRole, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className={styles.sortIconInactive}>↕</span>;
    return sortDir === 'desc'
      ? <ArrowDownwardIcon className={styles.sortIcon} />
      : <ArrowUpwardIcon className={styles.sortIcon} />;
  };

  const formatIP = (ip?: string): string => {
    if (!ip) return '-';
    if (ip.startsWith('::ffff:')) return ip.replace('::ffff:', '');
    if (ip === '::1') return 'localhost';
    return ip;
  };

  const getBrowserName = (userAgent?: string): string => {
    if (!userAgent) return '-';
    if (userAgent.includes('OPR/') && userAgent.includes('GX')) return 'Opera GX';
    if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) return 'Opera';
    if (userAgent.includes('Edg/') || userAgent.includes('Edge/')) return 'Edge';
    if (userAgent.includes('Brave/') || userAgent.includes('Brave')) return 'Brave';
    if (userAgent.includes('Chrome/') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Firefox/')) return 'Firefox';
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) return 'Safari';
    return userAgent.substring(0, 25) + '...';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getTimeSince = (dateString: string): string => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Agora';
    if (diffMins === 1) return '1 minuto atrás';
    if (diffMins < 60) return `${diffMins} minutos atrás`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hora atrás';
    if (diffHours < 24) return `${diffHours} horas atrás`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1 dia atrás';
    return `${diffDays} dias atrás`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainCard}>
        <div className={styles.cardHeader}>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por usuário, ID ou IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              disabled={loading}
            />
          </div>

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as any)}
            className={styles.filterSelect}
            disabled={loading}
          >
            <option value="all">Todos os Cargos</option>
            <option value="admin">Apenas Admins</option>
            <option value="supervisor">Apenas Supervisores</option>
            <option value="diretor">Apenas Diretores</option>
            <option value="user">Apenas Usuários</option>
          </select>
        </div>

        <div className={styles.tableWrapper}>
          {filteredSessions.length === 0 && !loading ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIconWrapper}>
                <DevicesIcon />
              </div>
              <p>Nenhuma sessão encontrada</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.sortableHeader} onClick={() => !loading && handleSort('usuario')}>
                    Usuário <SortIcon field="usuario" />
                  </th>
                  <th>Cargo</th>
                  <th>Base</th>
                  <th className={styles.sortableHeader} onClick={() => !loading && handleSort('loginTime')}>
                    Login <SortIcon field="loginTime" />
                  </th>
                  <th className={`${styles.sortableHeader} ${sortField === 'lastActivity' ? styles.activeHeader : ''}`} onClick={() => !loading && handleSort('lastActivity')}>
                    Última Ação <SortIcon field="lastActivity" />
                  </th>
                  <th>Status</th>
                  <th>Rede</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className={styles.skeletonRow}>
                      <td>
                        <div className={styles.userInfo}>
                          <div className={`${styles.avatar} ${styles.skeletonAvatar}`}></div>
                          <div className={styles.userDetails}>
                            <div className={styles.skeletonText} style={{ width: '120px', height: '14px' }}></div>
                            <div className={styles.skeletonText} style={{ width: '60px', height: '10px', marginTop: '6px' }}></div>
                          </div>
                        </div>
                      </td>
                      <td><div className={styles.skeletonText} style={{ width: '70px', height: '22px', borderRadius: '12px' }}></div></td>
                      <td><div className={styles.skeletonText} style={{ width: '40px' }}></div></td>
                      <td>
                        <div className={styles.timeInfo}>
                          <div className={styles.skeletonIcon} style={{ width: '16px', height: '16px', borderRadius: '50%' }}></div>
                          <div className={styles.timeDetails}>
                            <div className={styles.skeletonText} style={{ width: '110px', height: '12px' }}></div>
                            <div className={styles.skeletonText} style={{ width: '70px', height: '10px', marginTop: '4px' }}></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.timeInfo}>
                          <div className={styles.skeletonIcon} style={{ width: '16px', height: '16px', borderRadius: '50%' }}></div>
                          <div className={styles.timeDetails}>
                            <div className={styles.skeletonText} style={{ width: '110px', height: '12px' }}></div>
                            <div className={styles.skeletonText} style={{ width: '70px', height: '10px', marginTop: '4px' }}></div>
                          </div>
                        </div>
                      </td>
                      <td><div className={styles.skeletonText} style={{ width: '60px', height: '20px', borderRadius: '10px' }}></div></td>
                      <td>
                        <div className={styles.networkInfo} style={{ gap: '6px' }}>
                          <div className={styles.skeletonText} style={{ width: '90px', height: '18px', borderRadius: '4px' }}></div>
                          <div className={styles.skeletonText} style={{ width: '80px', height: '18px', borderRadius: '4px' }}></div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  filteredSessions.map(session => (
                    <tr key={session.userId} className={styles.tableRow}>
                      <td>
                        <div className={styles.userInfo}>
                        <div className={styles.avatar}>
                          {session.usuario.charAt(0).toUpperCase()}
                        </div>
                        <div className={styles.userDetails}>
                          <span className={styles.userName}>{session.usuario}</span>
                          <span className={styles.userId}>ID: {session.userId}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.roleBadge} ${styles[`role${session.role.charAt(0).toUpperCase() + session.role.slice(1)}`] || styles.roleUser}`}>
                        {session.role.charAt(0).toUpperCase() + session.role.slice(1)}
                      </span>
                    </td>
                    <td>
                      {session.base ? <span className={styles.badgeBase}>B{session.base}</span> : <span className={styles.badgeEmpty}>-</span>}
                    </td>
                    <td>
                      <div className={styles.timeInfo}>
                        <LoginIcon className={styles.timeIcon} />
                        <div className={styles.timeDetails}>
                          <span className={styles.timeVal}>{formatDate(session.loginTime)}</span>
                          <span className={styles.timeAgo}>{getTimeSince(session.loginTime)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.timeInfo}>
                        <AccessTimeIcon className={styles.timeIcon} />
                        <div className={styles.timeDetails}>
                          <span className={styles.timeVal}>{formatDate(session.lastActivity)}</span>
                          <span className={styles.timeAgo}>{getTimeSince(session.lastActivity)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.statusPill} ${session.status === 'online' ? styles.statusOnline : styles.statusOffline}`}>
                        <span className={styles.statusDot}></span>
                        {session.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.networkInfo}>
                        <div className={styles.ipBadge}>
                          <PublicIcon /> {formatIP(session.ip)}
                        </div>
                        <div className={styles.browserBadge} title={session.userAgent}>
                          <ComputerIcon /> {getBrowserName(session.userAgent)}
                        </div>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActiveSessionsPage;
